/**
 * 项目表格视图组件
 * 以表格形式展示项目列表，支持排序和快速操作
 * 行背景色根据预算使用率渐变着色
 */
import React from 'react';
import { Table, Tag, Progress, Space, Button, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  ExportOutlined,
  PlusOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { DashboardService } from '../../services/dashboardService';
import { getBudgetBasedColors } from '../../utils/colorUtils';

const { Text } = Typography;

export interface ProjectTableViewProps {
  projects: ProjectOverviewData[];
  loading?: boolean;
  onViewDetails?: (project: ProjectOverviewData) => void;
  onExport?: (project: ProjectOverviewData) => void;
  onAddData?: (project: ProjectOverviewData) => void;
  isMobile?: boolean;
}

// 项目状态配置
const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin />, text: '进行中' },
  '已完成': { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined />, text: '待开始' },
  '已暂停': { color: 'warning', icon: <PauseCircleOutlined />, text: '已暂停' }
};

export const ProjectTableView: React.FC<ProjectTableViewProps> = ({
  projects,
  loading = false,
  onViewDetails,
  onExport,
  onAddData,
  isMobile = false
}) => {
  // 格式化金额显示
  const formatMoney = (value: number): string => {
    if (value >= 10000) {
      return `¥${(value / 10000).toFixed(1)}万`;
    }
    return `¥${value.toLocaleString()}`;
  };

  // 桌面端列配置
  const desktopColumns: ColumnsType<ProjectOverviewData> = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      fixed: 'left',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => (
        <Tooltip title={name}>
          <Text strong style={{ fontSize: 13 }}>{name}</Text>
        </Tooltip>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: [
        { text: '进行中', value: '进行中' },
        { text: '已完成', value: '已完成' },
        { text: '待开始', value: '待开始' },
        { text: '已暂停', value: '已暂停' }
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: string) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG['待开始'];
        return (
          <Tag color={config.color} icon={config.icon} style={{ fontSize: 11 }}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: '预算使用',
      key: 'budget',
      width: 180,
      sorter: (a, b) => {
        const aPercent = a.budget > 0 ? a.actualCost / a.budget : 0;
        const bPercent = b.budget > 0 ? b.actualCost / b.budget : 0;
        return aPercent - bPercent;
      },
      render: (_, record) => {
        const health = DashboardService.calculateProjectHealth(record.budget, record.actualCost);
        const budgetColors = getBudgetBasedColors(record.budget, record.actualCost);
        return (
          <Tooltip title={`预算状态: ${budgetColors.statusText}`}>
            <div style={{ minWidth: 140 }}>
              <Progress
                percent={Math.min(health.percentage, 100)}
                size="small"
                strokeColor={health.color}
                format={() => `${health.percentage.toFixed(0)}%`}
              />
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                {formatMoney(record.actualCost)} / {formatMoney(record.budget)}
              </div>
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: '项目进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 140,
      sorter: (a, b) => a.progress - b.progress,
      render: (progress: number) => (
        <Progress
          percent={progress}
          size="small"
          strokeColor={progress >= 100 ? '#52c41a' : '#1677ff'}
        />
      )
    },
    {
      title: '剩余天数',
      dataIndex: 'daysRemaining',
      key: 'daysRemaining',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.daysRemaining - b.daysRemaining,
      render: (days: number, record) => {
        if (record.status === '已完成') {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Text type={days <= 7 ? 'danger' : days <= 30 ? 'warning' : undefined}>
            {days}天
          </Text>
        );
      }
    },
    {
      title: '数据统计',
      dataIndex: 'recentActivity',
      key: 'recentActivity',
      width: 160,
      ellipsis: true,
      render: (activity: string) => (
        <Tooltip title={activity}>
          <Text type="secondary" style={{ fontSize: 12 }}>{activity}</Text>
        </Tooltip>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails?.(record);
              }}
            />
          </Tooltip>
          <Tooltip title="导出">
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onExport?.(record);
              }}
            />
          </Tooltip>
          <Tooltip title="添加数据">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onAddData?.(record);
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // 移动端列配置（简化版）
  const mobileColumns: ColumnsType<ProjectOverviewData> = [
    {
      title: '项目',
      key: 'project',
      render: (_, record) => {
        const health = DashboardService.calculateProjectHealth(record.budget, record.actualCost);
        const statusConfig = STATUS_CONFIG[record.status] || STATUS_CONFIG['待开始'];
        return (
          <div style={{ padding: '4px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text strong style={{ fontSize: 13 }}>{record.name}</Text>
              <Tag color={statusConfig.color} style={{ fontSize: 10, marginRight: 0 }}>
                {statusConfig.text}
              </Tag>
            </div>
            <Progress
              percent={record.progress}
              size="small"
              strokeColor={record.progress >= 100 ? '#52c41a' : '#1677ff'}
              style={{ marginBottom: 4 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8c8c8c' }}>
              <span style={{ color: health.color }}>
                {formatMoney(record.actualCost)} / {formatMoney(record.budget)}
              </span>
              <span>{record.daysRemaining > 0 ? `剩余${record.daysRemaining}天` : '-'}</span>
            </div>
          </div>
        );
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails?.(record);
          }}
        >
          详情
        </Button>
      )
    }
  ];

  return (
    <Table
      columns={isMobile ? mobileColumns : desktopColumns}
      dataSource={projects}
      rowKey="id"
      loading={loading}
      size={isMobile ? 'small' : 'middle'}
      scroll={{ x: isMobile ? undefined : 1000 }}
      pagination={{
        pageSize: isMobile ? 10 : 15,
        showSizeChanger: !isMobile,
        showQuickJumper: !isMobile,
        showTotal: (total) => `共 ${total} 个项目`
      }}
      onRow={(record) => {
        const budgetColors = getBudgetBasedColors(record.budget, record.actualCost);
        return {
          onClick: () => onViewDetails?.(record),
          style: { 
            cursor: 'pointer',
            backgroundColor: budgetColors.backgroundColor,
            transition: 'all 0.3s ease'
          }
        };
      }}
    />
  );
};

export default ProjectTableView;
